import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apicallApi } from '../../../api/apicall';
import Navbar from '../../layout/Navbar';

function CreateApiCall() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    phase: 'setup', // 'setup' or 'cleanup'
    method: 'GET',
    url: '',
    body: '',
    trigger_url_pattern: '',  // New field
    trigger_http_method: '',  // New field
    parameters: [{ name: '', source: 'response', jsonPath: '', id: Date.now() }]
  });
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear validation error when field changes
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleBodyChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      body: value
    }));
    
    // Clear JSON validation error when body changes
    if (validationErrors.body) {
      setValidationErrors(prev => ({
        ...prev,
        body: null
      }));
    }
  };

  const handleParameterChange = (index, field, value) => {
    setFormData(prev => {
      const newParameters = [...prev.parameters];
      newParameters[index] = {
        ...newParameters[index],
        [field]: value
      };
      return {
        ...prev,
        parameters: newParameters
      };
    });
  };

  const addParameter = () => {
    setFormData(prev => ({
      ...prev,
      parameters: [...prev.parameters, { name: '', source: 'response', jsonPath: '', id: Date.now() }]
    }));
  };

  const removeParameter = (index) => {
    setFormData(prev => ({
      ...prev,
      parameters: prev.parameters.filter((_, i) => i !== index)
    }));
  };

  const validateJsonBody = () => {
    // Only validate if there is content and the method is not GET
    if (formData.body.trim() && formData.method !== 'GET') {
      try {
        JSON.parse(formData.body);
        return true;
      } catch (err) {
        setValidationErrors(prev => ({
          ...prev,
          body: 'Invalid JSON format'
        }));
        return false;
      }
    }
    return true; // No body or GET request is fine
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    
    if (!formData.url.trim()) {
      errors.url = 'URL is required';
    } else if (!/^https?:\/\/.+/.test(formData.url) && !formData.url.includes('{')) {
      errors.url = 'URL must start with http:// or https:// (unless using variables)';
    }

    // Validate trigger URL if provided
    if (formData.trigger_url_pattern && !formData.trigger_http_method) {
      errors.trigger_http_method = 'HTTP method is required when trigger URL is specified';
    }

    if (formData.trigger_http_method && !formData.trigger_url_pattern) {
      errors.trigger_url_pattern = 'Trigger URL is required when HTTP method is specified';
    }

    // Validate parameters
    const paramErrors = formData.parameters.map(param => {
      const pErrors = {};
      if (!param.name.trim()) pErrors.name = 'Name is required';
      if (!param.jsonPath.trim()) pErrors.jsonPath = 'JSON path is required';
      return pErrors;
    });

    if (paramErrors.some(e => Object.keys(e).length > 0)) {
      errors.parameters = paramErrors;
    }
    
    // Set all validation errors at once
    setValidationErrors(errors);
    
    // Return true if no errors and JSON is valid
    return Object.keys(errors).length === 0 && validateJsonBody();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Prepare request body - ensure all fields are included
      const apiCallData = {
        name: formData.name.trim(),
        phase: formData.phase,
        http_method: formData.method,
        url_template: formData.url.trim(),
        body_template: formData.body.trim() ? formData.body : null,  // Send raw string, let backend parse
        trigger_url_pattern: formData.trigger_url_pattern.trim() || null,
        trigger_http_method: formData.trigger_http_method || null,
        parameters: formData.parameters
          .filter(param => param.name.trim() && param.jsonPath.trim()) // Only send complete parameters
          .map(param => ({
            name: param.name.trim(),
            source: param.source,
            json_path: param.jsonPath.trim()
          }))
      };

      console.log('Submitting API call:', apiCallData); // For debugging
      
      await apicallApi.createApiCall(apiCallData);
      navigate('/api-calls');
    } catch (err) {
      console.error('Error creating API call:', err);
      setError(err.message || 'Failed to create API call');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center gap-2 mb-6">
          <button 
            onClick={() => navigate('/api-calls')}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to API Calls
          </button>
        </div>

        <h1 className="text-2xl font-bold text-gray-900 mb-6">Create API Call</h1>

        <div className="bg-white rounded-lg shadow-md p-6">
          {error && (
            <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6">
              <p>{error}</p>
            </div>
          )}
          
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full p-2 rounded border ${validationErrors.name ? 'border-red-500' : 'border-gray-300'} focus:border-black focus:outline-none`}
                  placeholder="My API Call"
                />
                {validationErrors.name && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.name}</p>
                )}
              </div>

              <div>
                <label htmlFor="phase" className="block text-sm font-medium text-gray-700 mb-2">
                  Phase
                </label>
                <select
                  id="phase"
                  name="phase"
                  value={formData.phase}
                  onChange={handleChange}
                  className="w-full p-2 rounded border border-gray-300 focus:border-black focus:outline-none"
                >
                  <option value="setup">Setup</option>
                  <option value="cleanup">Cleanup</option>
                </select>
              </div>

              <div>
                <label htmlFor="method" className="block text-sm font-medium text-gray-700 mb-2">
                  HTTP Method
                </label>
                <select
                  id="method"
                  name="method"
                  value={formData.method}
                  onChange={handleChange}
                  className="w-full p-2 rounded border border-gray-300 focus:border-black focus:outline-none"
                >
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                  <option value="DELETE">DELETE</option>
                  <option value="PATCH">PATCH</option>
                </select>
              </div>

              <div>
                <label htmlFor="url" className="block text-sm font-medium text-gray-700 mb-2">
                  URL Template *
                </label>
                <input
                  type="text"
                  id="url"
                  name="url"
                  value={formData.url}
                  onChange={handleChange}
                  className={`w-full p-2 rounded border ${validationErrors.url ? 'border-red-500' : 'border-gray-300'} focus:border-black focus:outline-none`}
                  placeholder="https://api.example.com/endpoint"
                />
                {validationErrors.url && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.url}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  You can use {'{parameter}'} syntax for dynamic values
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="trigger_url_pattern" className="block text-sm font-medium text-gray-700 mb-2">
                    Trigger URL Pattern
                  </label>
                  <input
                    type="text"
                    id="trigger_url_pattern"
                    name="trigger_url_pattern"
                    value={formData.trigger_url_pattern}
                    onChange={handleChange}
                    className={`w-full p-2 rounded border ${validationErrors.trigger_url_pattern ? 'border-red-500' : 'border-gray-300'} focus:border-black focus:outline-none`}
                    placeholder="/api/testsuite/create"
                  />
                  {validationErrors.trigger_url_pattern && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.trigger_url_pattern}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    The URL pattern that will trigger this API call
                  </p>
                </div>

                <div>
                  <label htmlFor="trigger_http_method" className="block text-sm font-medium text-gray-700 mb-2">
                    Trigger HTTP Method
                  </label>
                  <select
                    id="trigger_http_method"
                    name="trigger_http_method"
                    value={formData.trigger_http_method}
                    onChange={handleChange}
                    className={`w-full p-2 rounded border ${validationErrors.trigger_http_method ? 'border-red-500' : 'border-gray-300'} focus:border-black focus:outline-none`}
                  >
                    <option value="">Select a method</option>
                    <option value="GET">GET</option>
                    <option value="POST">POST</option>
                    <option value="PUT">PUT</option>
                    <option value="DELETE">DELETE</option>
                    <option value="PATCH">PATCH</option>
                  </select>
                  {validationErrors.trigger_http_method && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.trigger_http_method}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    The HTTP method that will trigger this API call
                  </p>
                </div>
              </div>

              <div>
                <label htmlFor="body" className="block text-sm font-medium text-gray-700 mb-2">
                  Body Template
                </label>
                <textarea
                  id="body"
                  name="body"
                  value={formData.body}
                  onChange={handleBodyChange}
                  rows="4"
                  className={`w-full p-2 rounded border ${validationErrors.body ? 'border-red-500' : 'border-gray-300'} focus:border-black focus:outline-none font-mono`}
                  placeholder="Enter JSON body template"
                />
                {validationErrors.body && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.body}</p>
                )}
                                  <p className="mt-1 text-xs text-gray-500">
                    Use {'{variable}'} syntax for dynamic values in JSON
                  </p>
              </div>

              <div>
                <div className="flex justify-between items-center mb-4">
                  <label className="block text-sm font-medium text-gray-700">Parameters</label>
                  <button
                    type="button"
                    onClick={addParameter}
                    className="bg-black text-white px-3 py-1 rounded-lg text-sm hover:bg-gray-800 transition-colors"
                  >
                    Add Parameter
                  </button>
                </div>

                <div className="space-y-4">
                  {formData.parameters.map((param, index) => (
                    <div key={param.id} className="flex gap-4 items-start bg-gray-50 p-4 rounded-lg">
                      <div className="flex-1">
                        <input
                          type="text"
                          value={param.name}
                          onChange={(e) => handleParameterChange(index, 'name', e.target.value)}
                          placeholder="Parameter name"
                          className={`w-full p-2 rounded border ${
                            validationErrors.parameters?.[index]?.name ? 'border-red-500' : 'border-gray-300'
                          } focus:border-black focus:outline-none`}
                        />
                        {validationErrors.parameters?.[index]?.name && (
                          <p className="mt-1 text-sm text-red-600">{validationErrors.parameters[index].name}</p>
                        )}
                      </div>

                      <div className="w-32">
                        <select
                          value={param.source}
                          onChange={(e) => handleParameterChange(index, 'source', e.target.value)}
                          className="w-full p-2 rounded border border-gray-300 focus:border-black focus:outline-none"
                        >
                          <option value="request">Request</option>
                          <option value="response">Response</option>
                        </select>
                      </div>

                      <div className="flex-1">
                        <input
                          type="text"
                          value={param.jsonPath}
                          onChange={(e) => handleParameterChange(index, 'jsonPath', e.target.value)}
                          placeholder="$.data.id"
                          className={`w-full p-2 rounded border ${
                            validationErrors.parameters?.[index]?.jsonPath ? 'border-red-500' : 'border-gray-300'
                          } focus:border-black focus:outline-none`}
                        />
                        {validationErrors.parameters?.[index]?.jsonPath && (
                          <p className="mt-1 text-sm text-red-600">{validationErrors.parameters[index].jsonPath}</p>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={() => removeParameter(index)}
                        className="text-red-600 hover:text-red-800 p-2"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`bg-black text-white px-6 py-2 rounded-lg hover:bg-gray-800 transition-colors ${
                    isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {isSubmitting ? 'Creating...' : 'Create API Call'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default CreateApiCall; 